import type { AdvicePortfolioOption } from "@/features/advice-portal/api/portfolios";
import { openCashFlow } from "@/features/portfolio-analysis/lib/cash-flow-store";
import { cardChoice } from "@/features/intelligence/lib/card-choice";
import type { HandlerOutcome } from "@/features/intelligence/lib/frontend-tool-run";
import type { PrepareCashFlowArgs } from "@/features/intelligence/lib/frontend-tools";
import {
  choiceResult,
  searchedInstead,
  shownResult,
} from "@/features/intelligence/lib/tool-result";

export async function prepareCashFlow(
  { seed, ignored, notAnId }: PrepareCashFlowArgs,
  toolCallId: string,
): Promise<HandlerOutcome> {
  openCashFlow(toolCallId, seed);
  const card = seed.kind === "withdrawal" ? "A withdrawal card" : "A deposit card";
  const choice = await cardChoice(seed, true, toolCallId);
  const against =
    choice.kind === "chosen" && seed.kind === "withdrawal"
      ? againstValue(seed.amount, choice.portfolio)
      : "within";
  if (against !== "within") {
    return shownResult(
      searchedInstead(
        `${card} is shown in the chat. ${against === "above" ? ABOVE_VALUE : NEAR_VALUE}. ${UNSEEN}`,
        seed,
        notAnId,
      ),
      against === "above"
        ? `the card shows why the withdrawal cannot be projected; ${KEEP_OUT}`
        : `the card shows the withdrawal; ${KEEP_OUT}`,
      ignored,
    );
  }
  if (choice.kind === "chosen") {
    return shownResult(
      searchedInstead(`${card} is shown in the chat. It ${DRAWN}. ${UNSEEN}`, seed, notAnId),
      `points to the card; ${KEEP_OUT}`,
      ignored,
    );
  }
  return choiceResult(
    choice,
    searchedInstead(
      `${card} is in the chat. Once the client and the portfolio are chosen, it ${DRAWN}. ` +
        UNSEEN,
      seed,
      notAnId,
    ),
    KEEP_OUT,
    ignored,
    "the client or the portfolio",
  );
}

function againstValue(
  amount: number,
  portfolio: AdvicePortfolioOption | null,
): "above" | "near" | "within" {
  const value = portfolio?.valueSek;
  if (value == null) return "within";
  if (amount > value * (1 + VALUE_MARGIN)) return "above";
  return amount < value * (1 - VALUE_MARGIN) ? "within" : "near";
}

const VALUE_MARGIN = 0.01;

const ABOVE_VALUE =
  "The withdrawal is larger than the portfolio's value today, so the card shows why no " +
  "projection can be made; nothing is drawn";

const NEAR_VALUE =
  "The withdrawal is close to the portfolio's value today, so the card either draws the " +
  "portfolio or, if the withdrawal is larger than the value it reads, shows why no projection " +
  "can be made";

const DRAWN =
  "draws the portfolio over ten years without and with the amount, expected and pessimistic, " +
  "from Quant's figures for today's portfolio, and offers the full analysis on Kundkortet › " +
  "Analys on the advisor's click";

const UNSEEN =
  "None of the figures reach you, nothing has been calculated or created, and there is no " +
  "probability of reaching any amount";

const KEEP_OUT =
  "do not state, estimate or describe any value or percentage yourself, do not call " +
  "analyze_portfolio or optimize_portfolio for it, and do not say an analysis has run";
